import { useState, useRef, useEffect } from 'react';

import Button from '@components/Button/Button';

import { getCurrencySymbol } from '../formatters';
import styles from '../style.module.scss';
import { type WorkforceType, type CurrencyType } from '../types';

function formatWithCommas(value: string): string {
  const digits = value.replace(/[^0-9]/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('en-GB');
}

interface CalculatorFormProps {
  formSubheading: string;
  workforceType: WorkforceType | null;
  totalEmployees: string;
  averageSalary: string;
  turnoverRate: string;
  currency: CurrencyType;
  errors: Record<string, string>;
  onWorkforceChange: (type: WorkforceType) => void;
  onTotalEmployeesChange: (value: string) => void;
  onAverageSalaryChange: (value: string) => void;
  onTurnoverRateChange: (value: string) => void;
  onCurrencyChange: (currency: CurrencyType) => void;
  onCalculate: () => void;
}

export function CalculatorForm({
  formSubheading,
  workforceType,
  totalEmployees,
  averageSalary,
  turnoverRate,
  currency,
  errors,
  onWorkforceChange,
  onTotalEmployeesChange,
  onAverageSalaryChange,
  onTurnoverRateChange,
  onCurrencyChange,
  onCalculate,
}: CalculatorFormProps) {
  const symbol = getCurrencySymbol(currency);
  const [cappedField, setCappedField] = useState<string | null>(null);
  const cappedTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(cappedTimer.current);
  }, []);

  const showCapped = (field: string) => {
    setCappedField(field);
    clearTimeout(cappedTimer.current);
    cappedTimer.current = setTimeout(() => setCappedField(null), 3000);
  };

  const clearCapped = () => {
    setCappedField(null);
    clearTimeout(cappedTimer.current);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onCalculate();
    }
  };

  const hasWorkforceError = errors.workforceType !== undefined;

  return (
    <div className={styles.formCard}>
      {formSubheading && <p className={styles.formSubheading}>{formSubheading}</p>}
      <p className={`${styles.formLabel} ${hasWorkforceError ? styles.formLabelError : ''}`}>
        Your workforce
        {hasWorkforceError && <span className={styles.formLabelHint}> — please select one</span>}
      </p>

      <div className={`${styles.radioGroup} ${hasWorkforceError ? styles.radioGroupError : ''}`}>
        <label
          aria-label="Mostly desk-based"
          className={`${styles.radioCard} ${workforceType === 'desk' ? styles.radioCardSelected : ''} ${hasWorkforceError ? styles.radioCardError : ''}`}
        >
          <input
            type="radio"
            name="workforce"
            checked={workforceType === 'desk'}
            onChange={() => onWorkforceChange('desk')}
            className={styles.radioInput}
          />
          <span className={styles.radioIndicator} />
          <div className={styles.radioContent}>
            <span className={styles.radioTitle}>Mostly desk-based</span>
            <span className={styles.radioDesc}>
              Professional services, financial services, technology, media
            </span>
          </div>
        </label>

        <label
          aria-label="Mostly frontline"
          className={`${styles.radioCard} ${workforceType === 'frontline' ? styles.radioCardSelected : ''} ${hasWorkforceError ? styles.radioCardError : ''}`}
        >
          <input
            type="radio"
            name="workforce"
            checked={workforceType === 'frontline'}
            onChange={() => onWorkforceChange('frontline')}
            className={styles.radioInput}
          />
          <span className={styles.radioIndicator} />
          <div className={styles.radioContent}>
            <span className={styles.radioTitle}>Mostly frontline</span>
            <span className={styles.radioDesc}>
              Retail, hospitality, logistics, healthcare, manufacturing
            </span>
          </div>
        </label>
      </div>

      <div className={styles.formGrid}>
        <div className={styles.inputField}>
          <label htmlFor="ee-calc-employees" className={styles.inputLabel}>
            Total employees
          </label>
          <input
            id="ee-calc-employees"
            type="text"
            inputMode="numeric"
            className={`${styles.input} ${errors.totalEmployees ? styles.inputError : ''}`}
            placeholder="eg. 6,000"
            value={formatWithCommas(totalEmployees)}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, '');
              if (digits === '' || Number(digits) <= 500000) {
                onTotalEmployeesChange(digits);
                clearCapped();
              } else {
                showCapped('employees');
              }
            }}
            onKeyDown={handleKeyDown}
          />
          {errors.totalEmployees && (
            <span className={styles.errorText}>{errors.totalEmployees}</span>
          )}
          {cappedField === 'employees' && !errors.totalEmployees && (
            <span className={styles.hintText}>Maximum 500,000</span>
          )}
        </div>

        <div className={styles.inputField}>
          <label htmlFor="ee-calc-salary" className={styles.inputLabel}>
            Average salary
          </label>
          <div
            className={`${styles.inputWithPrefix} ${averageSalary ? styles.inputHasValue : ''} ${errors.averageSalary ? styles.inputError : ''}`}
          >
            <span className={styles.inputPrefix}>{symbol}</span>
            <input
              type="text"
              inputMode="numeric"
              id="ee-calc-salary"
              className={styles.inputInner}
              placeholder="eg. 48,000"
              value={formatWithCommas(averageSalary)}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^0-9]/g, '');
                if (digits === '' || Number(digits) <= 500000) {
                  onAverageSalaryChange(digits);
                  clearCapped();
                } else {
                  showCapped('salary');
                }
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
          {errors.averageSalary && <span className={styles.errorText}>{errors.averageSalary}</span>}
          {cappedField === 'salary' && !errors.averageSalary && (
            <span className={styles.hintText}>Maximum {symbol}500,000</span>
          )}
          {!errors.averageSalary &&
            cappedField !== 'salary' &&
            currency === 'gbp' &&
            averageSalary &&
            Number(averageSalary) > 0 &&
            Number(averageSalary) < 30000 &&
            (() => {
              try {
                return navigator.language.startsWith('en-US');
              } catch {
                return false;
              }
            })() && (
              <span className={styles.contextNote}>
                Did you mean $
                {(Number(averageSalary) * 1.34).toLocaleString('en-GB', {
                  maximumFractionDigits: 0,
                })}
                ? Switch to USD if entering dollar amounts.
              </span>
            )}
        </div>

        <div className={styles.inputField}>
          <label htmlFor="ee-calc-turnover" className={styles.inputLabel}>
            Voluntary turnover rate (%)
          </label>
          <div
            className={`${styles.inputWithSuffix} ${turnoverRate ? styles.inputHasValue : ''} ${errors.turnoverRate ? styles.inputError : ''}`}
          >
            <input
              type="text"
              inputMode="decimal"
              id="ee-calc-turnover"
              className={styles.inputInner}
              placeholder="eg. 14"
              value={turnoverRate}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                const num = parseFloat(cleaned);
                if (cleaned === '' || cleaned === '.' || (num <= 60 && !/\..*\./.test(cleaned))) {
                  onTurnoverRateChange(cleaned);
                  clearCapped();
                } else {
                  showCapped('turnover');
                }
              }}
              onKeyDown={handleKeyDown}
            />
            <span className={styles.inputSuffix}>%</span>
          </div>
          {errors.turnoverRate && <span className={styles.errorText}>{errors.turnoverRate}</span>}
          {cappedField === 'turnover' && !errors.turnoverRate && (
            <span className={styles.hintText}>Maximum 60%</span>
          )}
        </div>

        <div className={styles.inputField}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- uses aria-labelledby on radiogroup */}
          <label id="ee-calc-currency-label" className={styles.inputLabel}>
            Currency
          </label>
          <div className={styles.currencyBorder}>
            <div
              className={styles.currencyToggle}
              role="radiogroup"
              aria-labelledby="ee-calc-currency-label"
            >
              <span
                className={styles.currencySlider}
                style={{ transform: currency === 'usd' ? 'translateX(100%)' : 'translateX(0)' }}
              />
              <button
                type="button"
                className={`${styles.currencyOption} ${currency === 'gbp' ? styles.currencyActive : ''}`}
                onClick={() => onCurrencyChange('gbp')}
              >
                £GBP
              </button>
              <button
                type="button"
                className={`${styles.currencyOption} ${currency === 'usd' ? styles.currencyActive : ''}`}
                onClick={() => onCurrencyChange('usd')}
              >
                $USD
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.calculateRow}>
        <Button variant="primary" size="medium" className={styles.calcButton} onClick={onCalculate}>
          Calculate
        </Button>
      </div>
    </div>
  );
}
